import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ArchiveButton } from "@/components/ArchiveButton";
import { Lightbulb, Plus, ThumbsUp, Trophy, Sparkles, Flame, Rocket, Award } from "lucide-react";
import { toast } from "sonner";

const CAT = { operasional: "Operasional", layanan: "Layanan", penjualan: "Penjualan", efisiensi: "Efisiensi", inovasi: "Inovasi" };
const STATUS_CLS = { diajukan: "bg-slate-100 text-slate-600", disetujui: "bg-emerald-100 text-emerald-700", ditolak: "bg-rose-100 text-rose-700", diterapkan: "bg-indigo-100 text-indigo-700" };
const BADGE_ICON = { pemula: Sparkles, produktif: Flame, juara: Trophy, inovator: Rocket };

export default function Initiatives() {
  const { user } = useAuth();
  const canDecide = ["atasan", "owner", "kepala_divisi", "super_admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [board, setBoard] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "operasional", impact: "" });
  const [decideItem, setDecideItem] = useState(null);
  const [score, setScore] = useState(70);
  const [note, setNote] = useState("");

  const load = () => { api.get("/initiatives").then((r) => setItems(r.data)); api.get("/initiatives/leaderboard").then((r) => setBoard(r.data)); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim() || !form.description.trim()) return toast.error("Judul & deskripsi wajib");
    await api.post("/initiatives", form); toast.success("Inisiatif diajukan");
    setCreateOpen(false); setForm({ title: "", description: "", category: "operasional", impact: "" }); load();
  };
  const vote = async (id) => { const r = await api.post(`/initiatives/${id}/vote`); setItems((a) => a.map((i) => i.id === id ? { ...i, vote_count: r.data.vote_count } : i)); };
  const decide = async (decision) => {
    await api.post(`/initiatives/${decideItem.id}/decision`, { decision, score: decision === "tolak" ? 0 : score, note });
    toast.success("Inisiatif ditinjau"); setDecideItem(null); setNote(""); setScore(70); load();
  };

  const catalog = {}; (board?.badges_catalog || []).forEach((b) => { catalog[b.id] = b; });

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Lightbulb className="h-7 w-7 text-indigo-500" />Program Inisiatif</h1>
          <p className="text-slate-500 text-sm">Dinding Inisiatif, apresiasi rekan, skor & lencana, leaderboard</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-initiative-btn" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1.5" />Ajukan Inisiatif</Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Wall */}
        <div className="lg:col-span-2">
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Dinding Inisiatif</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {items.map((it) => (
              <div key={it.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col" data-testid={`initiative-card-${it.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-bold text-slate-800 leading-snug">{it.title}</p>
                  <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold", STATUS_CLS[it.status])}>{it.status}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1 flex-1">{it.description}</p>
                {it.impact && <p className="text-xs text-emerald-600 mt-1">Dampak: {it.impact}</p>}
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
                  <span className="text-xs text-slate-400">{it.author_name} · {CAT[it.category]}{it.score ? ` · skor ${it.score}` : ""}</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => vote(it.id)} className={cn("inline-flex items-center gap-1 text-xs font-semibold", (it.votes || []).includes(user?.id) ? "text-indigo-600" : "text-slate-400")} data-testid={`vote-btn-${it.id}`}>
                      <ThumbsUp className="h-3.5 w-3.5" />{it.vote_count}
                    </button>
                    {canDecide && it.status === "diajukan" && <Button size="sm" className="h-6 text-xs" onClick={() => { setDecideItem(it); }} data-testid={`review-initiative-${it.id}`}>Nilai</Button>}
                    {(canDecide || it.user_id === user?.id) && <ArchiveButton url={`/initiatives/${it.id}`} label="inisiatif ini" onDone={load} testid={`archive-initiative-${it.id}`} />}
                  </div>
                </div>
              </div>
            ))}
            {items.length === 0 && <p className="text-slate-400 col-span-full text-center py-12">Belum ada inisiatif.</p>}
          </div>
        </div>

        {/* Leaderboard */}
        <div>
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-1.5"><Trophy className="h-4 w-4 text-amber-500" />Leaderboard</h3>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3" data-testid="initiative-leaderboard">
            {(board?.leaderboard || []).map((r, i) => (
              <div key={r.user_id} className="flex items-center gap-2.5" data-testid={`leaderboard-row-${r.user_id}`}>
                <span className="text-sm w-5 text-center">{["🥇", "🥈", "🥉"][i] || i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{r.name}</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    {r.badges.map((b) => { const Ic = BADGE_ICON[b] || Award; return <Ic key={b} className="h-3.5 w-3.5 text-amber-500" title={catalog[b]?.label} data-testid={`badge-${r.user_id}-${b}`} />; })}
                  </div>
                </div>
                <div className="text-right"><p className="text-sm font-extrabold text-indigo-600">{r.score}</p><p className="text-[10px] text-slate-400">{r.approved} disetujui</p></div>
              </div>
            ))}
            {(board?.leaderboard || []).length === 0 && <p className="text-sm text-slate-400 italic">Belum ada peserta.</p>}
          </div>
          {board?.badges_catalog && (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Katalog Lencana</p>
              {board.badges_catalog.map((b) => { const Ic = BADGE_ICON[b.id] || Award; return (
                <div key={b.id} className="flex items-center gap-2 text-xs mb-1.5"><Ic className="h-4 w-4 text-amber-500" /><span className="font-medium text-slate-700">{b.label}</span><span className="text-slate-400">— {b.rule}</span></div>
              ); })}
            </div>
          )}
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Ajukan Inisiatif</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Judul</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="initiative-title-input" /></div>
            <div><Label>Deskripsi</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="initiative-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="initiative-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CAT).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Dampak Diharapkan</Label><Input value={form.impact} onChange={(e) => setForm((f) => ({ ...f, impact: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Batal</Button><Button onClick={create} data-testid="initiative-create-submit" className="bg-indigo-600 hover:bg-indigo-700">Ajukan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!decideItem} onOpenChange={(o) => !o && setDecideItem(null)}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Nilai: {decideItem?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Skor (0–100)</Label><Input type="number" min={0} max={100} value={score} onChange={(e) => setScore(Number(e.target.value))} data-testid="initiative-score-input" /></div>
            <div><Label>Catatan</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="initiative-note-input" /></div>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" className="text-rose-600" onClick={() => decide("tolak")} data-testid="initiative-reject-btn">Tolak</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decide("setujui")} data-testid="initiative-approve-btn">Setujui</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => decide("diterapkan")} data-testid="initiative-apply-btn">Terapkan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
